import React from 'react';
import { useSelector } from 'react-redux';
import useFeatureFlag from 'src/common/hooks/useFeatureFlag';
import { getAuthenticatedUserRole } from 'src/common/utils/reduxStoreSelectors';
import type { UserRole } from 'src/types/user/User';
import { EXTERNAL_PATHS, PATHS } from '../../constants';
import ChargeNavItem from './ChargeNavItem';
import InboxNavItem from './InboxNavItem';
import LogoutNavItem from './LogoutNavItem';
import MyListingsNavItem from './MyListingsNavItem';
import SubNavHeading from './SubNavHeading';
import SubNavUserDetails from './SubNavUserDetails';
import type {
  MainNavItemComponentType, MainNavItemLinkType,
  MainNavItemType,
  NavItemLinkType,
  NavItemType,
} from './types';

const charge: MainNavItemComponentType = {
  key: 'charge',
  component: <ChargeNavItem />,
  roles: ['owner'],
  isHeaderNavItem: {
    owner: ['xs'], // Shown on extra small screens for owners
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
  },
};
const myListings: MainNavItemComponentType = {
  key: 'my-listings',
  component: <MyListingsNavItem />,
  roles: ['owner'],
  isHeaderNavItem: {
    owner: ['xs'],
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
  },
};
export const directBooking: NavItemLinkType = {
  key: 'direct-booking',
  label: 'Direct Booking',
  path: EXTERNAL_PATHS.CREATE_DIRECT_BOOKING,
  icon: 'credit-card-arrow',
  roles: ['owner'],
};
export const addOn: NavItemLinkType = {
  key: 'add-on-payment',
  label: 'Add-On Payment',
  path: EXTERNAL_PATHS.CHARGE_ADD_ON,
  icon: 'credit-card-check',
  roles: ['owner'],
  sectionDivider: true,
};
const inbox: MainNavItemComponentType = {
  key: 'inbox',
  component: <InboxNavItem />,
  roles: ['owner', 'renter'],
  isHeaderNavItem: {
    owner: ['xs'], // Shown on extra small screens for owners
    renter: ['xs'], // Shown on extra small screens for renters
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
    renter: ['m'],
  },
};
const inboxSubNav: NavItemLinkType = {
  key: 'inbox',
  label: 'Inbox',
  path: PATHS.INBOX,
  icon: 'envelope',
  roles: ['owner', 'renter'],
};
const calendar: MainNavItemLinkType = {
  key: 'calendar',
  label: 'Calendar',
  path: PATHS.CALENDAR,
  icon: 'calendar',
  roles: ['owner'],
  isHeaderNavItem: {
    owner: ['xs'], // Shown on extra small screens for owners
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
  },
};
const ownerAdmin: MainNavItemLinkType = {
  key: 'admin',
  label: 'Admin',
  path: EXTERNAL_PATHS.ADMIN_LISTINGS,
  icon: 'admin',
  roles: ['owner'],
  isHeaderNavItem: {
    owner: ['s'], // Shown on small screens for owners
  },
  iconOnlyScreenSizes: {
    owner: ['l'],
  },
};
const favorites: NavItemLinkType = {
  key: 'favorites',
  label: 'Favorites',
  path: EXTERNAL_PATHS.FAVORITES,
  icon: 'heart',
  sectionDivider: true,
  roles: ['captain', 'owner', 'renter'],
};
const listYourBoat: NavItemLinkType = {
  key: 'list-your-boat',
  label: 'List Your Boat',
  path: EXTERNAL_PATHS.ADMIN_LISTINGS,
  icon: 'boat-add',
  roles: ['captain', 'renter'],
};
const accountCaptainOwner: NavItemLinkType = {
  key: 'account',
  label: 'Account',
  path: EXTERNAL_PATHS.ADMIN_ACCOUNT,
  icon: 'admin',
  roles: ['captain', 'owner'],
};
const accountRenter: NavItemLinkType = {
  key: 'account',
  label: 'Account',
  path: EXTERNAL_PATHS.ADMIN_PROFILE,
  icon: 'gear',
  roles: ['renter'],
};
const help: NavItemLinkType = {
  key: 'help',
  label: 'Help',
  path: 'https://getmyboat.zendesk.com/',
  openInNewTab: true,
  icon: 'question',
  sectionDivider: true,
  roles: ['owner', 'captain', 'renter'],
};
const chargeSubNav = [
  {
    key: 'charge-heading',
    component: <SubNavHeading>Charge</SubNavHeading>,
  },
  directBooking,
  addOn,
];

const myListingsSubNav: NavItemType[] = [
  {
    key: 'my-listings-heading',
    component: <SubNavHeading>My Listings</SubNavHeading>,
  },
  {
    key: 'my-listings--boats',
    label: 'Boats & Pricing',
    path: EXTERNAL_PATHS.ADMIN_LISTINGS,
    icon: 'boat',
    roles: ['owner'],
  },
  {
    key: 'my-listings--add-ons',
    label: 'Add-On Manager',
    path: EXTERNAL_PATHS.ADMIN_ADDONS,
    icon: 'hand-coins',
    roles: ['owner'],
    sectionDivider: true,
  },
];

const useNavigationConfig = (): (MainNavItemType | NavItemType)[] => {
  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');
  const adminOrMyListings = isNavUpdateEnabled ? myListings : ownerAdmin;

  return [
    charge,
    inbox,
    calendar,
    adminOrMyListings,
    favorites,
    listYourBoat,
    accountCaptainOwner,
    accountRenter,
    help,
  ];
};

const renterConfigItemsOrdered: NavItemType[] = [
  inboxSubNav,
  {
    ...favorites,
    sectionDivider: false,
  },
  accountRenter,
  {
    ...listYourBoat,
    sectionDivider: true,
  },
  help,
];

const captainConfigItemsOrdered: NavItemType[] = [
  inboxSubNav,
  {
    ...favorites,
    sectionDivider: false,
  },
  accountCaptainOwner,
  {
    ...listYourBoat,
    sectionDivider: true,
  },
  help,
];

export const getOwnerSubNavConfig = (isNavUpdateEnabled?: boolean): NavItemType[] => [
  ...chargeSubNav,
  inboxSubNav,
  calendar,
  ...(isNavUpdateEnabled ? [] : [ownerAdmin]),
  favorites,
  ...(isNavUpdateEnabled ? myListingsSubNav : []),
  ...(isNavUpdateEnabled ? [accountCaptainOwner] : []),
  help,
];

// Helper to get authed menu items based on role and feature flags
export const getAuthenticatedSubNavItems = (
  userRole: UserRole,
  isNavUpdateEnabled?: boolean,
): NavItemType[] => {
  switch (userRole) {
    case 'captain':
      return captainConfigItemsOrdered;
    case 'owner':
      return getOwnerSubNavConfig(isNavUpdateEnabled);
    case 'renter':
      return renterConfigItemsOrdered;
    default:
      return [];
  }
};

const getNestedAuthMenuItems = (
  userRole: UserRole,
  isNavUpdateEnabled?: boolean,
): NavItemType[] => [
  {
    key: 'root',
    children: [
      {
        key: 'user-details',
        component: <SubNavUserDetails />,
        sectionDivider: true,
      },
      ...(getAuthenticatedSubNavItems(userRole, isNavUpdateEnabled)),
      {
        key: 'logout',
        component: <LogoutNavItem />,
      },
    ],
  },
];

export const useSubNavigationConfig = (): NavItemType[] => {
  const isNavUpdateEnabled = useFeatureFlag('pricing-v2--nav');
  const userRole = useSelector(getAuthenticatedUserRole);

  // Default to 'renter' when userRole is null (e.g., during initial load or unauthenticated state)
  return getNestedAuthMenuItems(userRole ?? 'renter', isNavUpdateEnabled);
};

export default useNavigationConfig;
